import React, { createContext, useState, useEffect } from 'react';
import axios from 'axios';
import { serverUrl } from '../Constant/Static/config';

const AuthContext = createContext();

const AuthProvider = ({ children }) => {
    const [auth, setAuth] = useState(() => {
        const user = JSON.parse(localStorage.getItem('userToken')) || null;
        const admin = JSON.parse(sessionStorage.getItem('adminToken')) || null;
        const verifier = JSON.parse(sessionStorage.getItem('verifierToken')) || null;

        return {
            isAuthenticated: !!user || !!admin || !!verifier,
            isAdminAuthenticated: !!admin,
            isVerifierAuthenticated: !!verifier,
            user: user || admin || verifier || null,
            role: user ? 'user' : admin ? 'admin' : verifier ? 'verifier' : '',
        };
    });


    const loginUser = async (userData) => {
        try {
            sessionStorage.removeItem('adminToken');
            sessionStorage.removeItem('verifierToken');
            localStorage.setItem('userToken', JSON.stringify(userData));

            if (localStorage.getItem('currentSection') !== null) {
                localStorage.removeItem('currentSection');
            }

            const response = await axios.post(`${serverUrl}/api/protectedUser`, {}, {
                headers: { 'Authorization': `Bearer ${userData}` },
            });

            if (response.status === 200) {
                setAuth({
                    isAuthenticated: true,
                    isAdminAuthenticated: false,
                    isVerifierAuthenticated: false,
                    user: response.data.userInfo,
                    role: 'user',
                });
            }
        } catch (error) {
            console.error("User login failed:", error);
        }
    };

    const loginVerifier = async (verifierData) => {
        try {
            localStorage.removeItem('userToken');
            sessionStorage.removeItem('adminToken');
            sessionStorage.setItem('verifierToken', JSON.stringify(verifierData));

            const response = await axios.post(`${serverUrl}/api/cv/verifier/protected`, {}, {
                headers: { 'Authorization': `Bearer ${verifierData}` },
            });

            if (response.status === 200) {
                setAuth({
                    isAuthenticated: false,
                    isAdminAuthenticated: false,
                    isVerifierAuthenticated: true,
                    user: response.data.userInfo,
                    role: 'verifier',
                });
            }
        } catch (error) {
            console.error("Verifier login failed:", error);
        }
    };

    const loginAdmin = async (adminData) => {
        try {
            localStorage.removeItem('userToken');
            sessionStorage.removeItem('verifierToken');
            sessionStorage.setItem('adminToken', JSON.stringify(adminData));

            const response = await axios.post(`${serverUrl}/api/admin/protected`, {}, {
                headers: { 'Authorization': `Bearer ${adminData}` },
            });

            if (response.status === 200) {
                setAuth({
                    isAuthenticated: false,
                    isAdminAuthenticated: true,
                    isVerifierAuthenticated: false,
                    user: response.data.userInfo,
                    role: 'admin',
                });
            }
        } catch (error) {
            console.error("Admin login failed:", error);
        }
    };

    const checkSession = async () => {
        try {
            const userToken = JSON.parse(localStorage.getItem('userToken')) || null;
            const adminToken = JSON.parse(sessionStorage.getItem('adminToken')) || null;
            const verifierToken = JSON.parse(sessionStorage.getItem('verifierToken')) || null;

            if (userToken) {
                await validateToken(userToken, 'user', `${serverUrl}/api/protectedUser`);
            } else if (adminToken) {
                await validateToken(adminToken, 'admin', `${serverUrl}/api/admin/protected`);
            } else if (verifierToken) {
                await validateToken(verifierToken, 'verifier', `${serverUrl}/api/cv/verifier/protected`);
            } else {
                resetAuth();
            }
        } catch (error) {
            console.error("Session validation error:", error);
            resetAuth();
        }
    };

    const validateToken = async (token, role, url) => {
        try {
            const response = await axios.post(url, {}, {
                headers: { 'Authorization': `Bearer ${token}` },
            });

            if (response.status === 200 && response.data?.userInfo) {
                setAuth({
                    isAuthenticated: true,
                    isAdminAuthenticated: role === 'admin',
                    isVerifierAuthenticated: role === 'verifier',
                    user: response.data.userInfo,
                    role: role,
                });
            } else {
                handleInvalidToken(role);
            }
        } catch (error) {
            console.error(`${role} token validation failed:`, error);
            handleInvalidToken(role);
        }
    };

    const handleInvalidToken = (role) => {
        const tokenKey = role === 'user' ? 'userToken' : role === 'admin' ? 'adminToken' : 'verifierToken';
        if (role === 'user') localStorage.removeItem(tokenKey);
        else sessionStorage.removeItem(tokenKey);
        resetAuth();
    };

    useEffect(() => {
        try {
            checkSession();
        } catch (error) {
            console.error("Error in useEffect:", error);
        }
    }, []);

    const logout = async (url, tokenKey) => {
        try {
            await axios.post(url, {}, { withCredentials: true });
            if (localStorage.getItem('currentSection') !== null) {
                localStorage.removeItem('currentSection');
                localStorage.removeItem('sex');
                localStorage.removeItem('typeuser')
            }
            if(localStorage.getItem('sex') !== null)
            if (tokenKey === 'userToken') {
                localStorage.removeItem(tokenKey);
            }
            else sessionStorage.removeItem(tokenKey);
        } catch (error) {
            console.error(`Logout failed:`, error);
        } finally {
            resetAuth();
        }
    };

    const logoutUser = async () => await logout(`${serverUrl}/api/logout`, 'userToken');
    const logoutAdmin = async () => await logout(`${serverUrl}/api/admin/logout`, 'adminToken');
    const logoutVerifier = async () => await logout(`${serverUrl}/api/cv/verifier/logout`, 'verifierToken');

    const resetAuth = () => {
        setAuth({
            isAuthenticated: false,
            isAdminAuthenticated: false,
            isVerifierAuthenticated: false,
            user: null,
            role: '',
        });
    };

    return (
        <AuthContext.Provider
            value={{
                auth,
                loginUser,
                loginAdmin,
                loginVerifier,
                logoutUser,
                logoutAdmin,
                logoutVerifier,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export { AuthContext, AuthProvider };
